import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { skyLight } from '../../../../styles/design-tokens';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { addTrendLines } from '../allocation-of-costs/allocation-of-costs.helpers';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as CostByInitiativeLegend } from '../shared/Legend';
import type { DataPoint } from '../shared/shared.type';
import { buildChartLegend, getItemColor } from './cost-by-initiative.helpers';

function Chart({
  chartData,
  initiativeInfo,
  chartRef,
}: { chartData: DataPoint[]; initiativeInfo: { [id: string]: string }; chartRef: React.RefObject<HTMLDivElement> }) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { selectedInitiativeIds, selectedTrendLines } = useFinancialsStore((state) => state.filters.costByInitiative);

  const chartDataWithTrendLines = addTrendLines(chartData);

  const legendPayload = buildChartLegend(selectedInitiativeIds, initiativeInfo, DEFAULT_CHART_COLORS);

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer>
        <ComposedChart data={chartDataWithTrendLines} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => dayjs(value).format('MMM D')}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            padding={{ left: 40, right: 40 }}
          />
          <YAxis
            orientation="left"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(value) => `$${value}`}
          />
          <CustomTooltip
            filterFn={(item) => Boolean(item.name) && selectedInitiativeIds.includes(item.name!)}
            nameFormatter={(item) => initiativeInfo[item.name as keyof typeof initiativeInfo]}
            valueFormatter={(item) => `$${item.value}`}
          />
          {selectedInitiativeIds.map((initiativeId) => {
            const color = getItemColor(initiativeId, selectedInitiativeIds, DEFAULT_CHART_COLORS);
            return (
              <Fragment key={initiativeId}>
                <Line
                  type="monotone"
                  dataKey={`${initiativeId}_trend`}
                  stroke={color}
                  strokeDasharray="5 5"
                  strokeWidth={hoveredItem === null || hoveredItem === initiativeId ? 2 : 1}
                  opacity={hoveredItem === null || hoveredItem === initiativeId ? 1 : 0.3}
                  fill={color}
                  key={`${initiativeId}_trend`}
                  hide={!selectedTrendLines.includes(initiativeId)}
                />
                <Bar
                  dataKey={initiativeId}
                  opacity={hoveredItem === null || hoveredItem === initiativeId ? 1 : 0.3}
                  fill={color}
                  key={initiativeId}
                />
              </Fragment>
            );
          })}
          <Legend
            payload={legendPayload}
            content={<CostByInitiativeLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />}
            verticalAlign="bottom"
            height={50}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Chart };
